import React, { useState } from "react";
import {
  Modal,
  TextInput,
  NumberInput,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Text,
  CopyButton,
  ActionIcon,
  Tooltip,
  Code,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { IconCopy, IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { bridgeService } from "../../services/bridgeService";
import { useFacilities } from "../../hooks/useReferenceData";

interface Props {
  opened: boolean;
  onClose: () => void;
}

const getBackendHostname = (): string => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (apiBaseUrl) {
    try {
      return new URL(apiBaseUrl).hostname;
    } catch {
      return apiBaseUrl.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    }
  }
  return window.location.hostname;
};

const RegisterWorkstationModal: React.FC<Props> = ({ opened, onClose }) => {
  const [name, setName] = useState("");
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [localPort, setLocalPort] = useState<number>(8765);
  const [loading, setLoading] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const backendHostname = getBackendHostname();
  const { data: facilities = [] } = useFacilities();

  const handleClose = () => {
    setName("");
    setFacilityId(null);
    setLocalPort(8765);
    setGeneratedApiKey(null);
    onClose();
  };

  const handleRegister = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await bridgeService.createWorkstation({
        name: name.trim(),
        facilityId: facilityId || undefined,
        localPort,
      });
      setGeneratedApiKey(res.data.apiKey);
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: "Workstation Registered",
        message: `"${name.trim()}" has been registered successfully.`,
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Registration Failed",
        message:
          err?.response?.data?.message || "Failed to register workstation",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Register Workstation"
      size="md"
    >
      <Stack>
        {!generatedApiKey ? (
          <>
            <TextInput
              label="Workstation Name"
              placeholder="e.g. Lab Station 1"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRegister();
              }}
            />
            <Select
              label="Facility"
              placeholder="Select facility"
              data={facilities.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
              value={facilityId}
              onChange={setFacilityId}
              searchable
              clearable
            />
            <NumberInput
              label="Local Bridge Port"
              description="Port the bridge app listens on for frontend connections"
              value={localPort}
              onChange={(val) => setLocalPort(Number(val) || 8765)}
              min={1024}
              max={65535}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleRegister}
                loading={loading}
                disabled={!name.trim()}
              >
                Register
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Save your API key"
              color="yellow"
            >
              This key will only be shown once. Copy it now and store it
              securely — it cannot be retrieved later.
            </Alert>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Backend URL
              </Text>
              <Group gap="xs" align="center">
                <Code
                  style={{
                    flex: 1,
                    wordBreak: "break-all",
                    padding: "8px 12px",
                    fontSize: 13,
                  }}
                >
                  {backendHostname}
                </Code>
                <CopyButton value={backendHostname} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied!" : "Copy backend URL"}
                      withArrow
                    >
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        variant="subtle"
                        onClick={copy}
                        size="lg"
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                API Key
              </Text>
              <Group gap="xs" align="center">
                <Code
                  style={{
                    flex: 1,
                    wordBreak: "break-all",
                    padding: "8px 12px",
                    fontSize: 13,
                  }}
                >
                  {generatedApiKey}
                </Code>
                <CopyButton value={generatedApiKey} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied!" : "Copy API key"}
                      withArrow
                    >
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        variant="subtle"
                        onClick={copy}
                        size="lg"
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Stack>
            <Group justify="flex-end">
              <Button onClick={handleClose}>Done</Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default RegisterWorkstationModal;
